import { RouteError } from '@/components/RouteError';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Loading } from '@/components/ui/Loading';
import { AuthGuard } from '@/features/auth/AuthGuard';
import { type ComponentType, Suspense, lazy } from 'react';
import { Navigate, type RouteObject, RouterProvider, createBrowserRouter } from 'react-router-dom';

/**
 * lazyPage — wrapper na React.lazy z auto-recovery dla nieswiezych chunkow.
 *
 * Problem: po deployu nowej wersji frontendu (CF Pages), uzytkownik moze miec
 * w cache stary `index.html` ktory referencuje stare chunk hashes (np.
 * `BasketPage-abc123.js`). Ten plik juz nie istnieje na CF Pages — zwraca 404
 * lub HTML index page → import() resolve'uje sie z `m = undefined` lub
 * `m.BasketPage = undefined`. Potem `<BasketPage />` rzuca:
 * "Cannot read properties of undefined (reading 'BasketPage')".
 *
 * Fix: w then-handler walidujemy czy named export istnieje. Gdy nie — robimy
 * automatyczny reload (sessionStorage flag zapobiega petli reload-fail-reload).
 * Po reloadzie browser pobiera fresh `index.html` z aktualnymi chunk hashami.
 *
 * Bonus: `vite:preloadError` event handler w main.tsx nadal lapie pre-load
 * fail, ale on dotyczy tylko `<link rel="modulepreload">` ktory dispatchuje
 * event. Tutaj lapiemy actual `import()` failure ktory `vite:preloadError`
 * moze pominac.
 */
const RELOAD_FLAG_KEY = 'lazy-reload-attempt';

/**
 * Najczestsza przyczyna nieswiezego chunku (missing export / parse error) to
 * STARY service worker serwujacy stale precache — przezywa Ctrl+Shift+R, bo
 * zyje poza cache HTTP. Zanim przeladujemy, wyrejestrowujemy WSZYSTKIE SW i
 * kasujemy cache, zeby reload poszedl czysto z sieci. To auto-leczy usera bez
 * recznego "Clear site data" w DevTools (Dawid 2026-07-24 — katastrofa /crm).
 */
async function nukeServiceWorkersAndCaches(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    // brak SW / brak uprawnien — ignorujemy, reload i tak sprobuje
  }
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // CacheStorage niedostepny — ignorujemy
  }
}

function lazyPage<TName extends string>(
  loader: () => Promise<
    Partial<Record<TName, ComponentType<unknown>>> & { default?: ComponentType<unknown> }
  >,
  name: TName,
) {
  return lazy(async () => {
    try {
      const m = await loader();
      const component = m?.[name] ?? m?.default;
      if (typeof component !== 'function' && typeof component !== 'object') {
        throw new Error(`Lazy module missing export: ${name}`);
      }
      // Sukces — czyszczenie flagi po udanej iteracji
      try {
        sessionStorage.removeItem(RELOAD_FLAG_KEY);
      } catch {
        // sessionStorage moze byc disabled (private mode itp) — ignorujemy
      }
      return { default: component };
    } catch (err) {
      // Stary chunk po nowym deployu. Reload pobiera fresh index.html. Robimy
      // to TYLKO raz per session — gdy juz reload byl a chunk nadal nie ladowal,
      // propagujemy blad zeby ErrorBoundary go pokazal (zamiast loop'owac).
      let alreadyTried = false;
      try {
        alreadyTried = sessionStorage.getItem(RELOAD_FLAG_KEY) === '1';
        sessionStorage.setItem(RELOAD_FLAG_KEY, '1');
      } catch {
        // ignorujemy — bez sessionStorage robimy 1 reload bez ochrony loop
      }
      if (!alreadyTried) {
        console.warn(`[lazyPage] ${name} chunk fail — czyszcze SW/cache i reload`, err);
        // Wyrejestruj stary service worker + wyczysc cache PRZED reloadem —
        // inaczej reload znowu dostaje stale chunki i user utyka mimo Ctrl+Shift+R.
        await nukeServiceWorkersAndCaches();
        window.location.reload();
        // Promise nigdy nie resolve'uje (browser przeladuje strone) — placeholder
        // zeby TypeScript byl szczesliwy.
        return new Promise<never>(() => {});
      }
      throw err;
    }
  });
}

// Lazy-loaded pages — dla code splitting (z auto-recovery przez lazyPage)
const CrmPage = lazyPage(() => import('@/pages/CrmPage'), 'CrmPage');
const LeadNewPage = lazyPage(() => import('@/pages/LeadNewPage'), 'LeadNewPage');
const LeadDetailPage = lazyPage(() => import('@/pages/LeadDetailPage'), 'LeadDetailPage');
const BasketPage = lazyPage(() => import('@/pages/BasketPage'), 'BasketPage');
const SalesDashboardPage = lazyPage(
  () => import('@/pages/SalesDashboardPage'),
  'SalesDashboardPage',
);
const MarketingDashboardPage = lazyPage(
  () => import('@/pages/MarketingDashboardPage'),
  'MarketingDashboardPage',
);
const ProjectsPage = lazyPage(() => import('@/pages/ProjectsPage'), 'ProjectsPage');
const ProjectDetailPage = lazyPage(() => import('@/pages/ProjectDetailPage'), 'ProjectDetailPage');
const RealizationsPage = lazyPage(() => import('@/pages/RealizationsPage'), 'RealizationsPage');
const RealizationDetailPage = lazyPage(
  () => import('@/pages/RealizationDetailPage'),
  'RealizationDetailPage',
);
const ReportsPage = lazyPage(() => import('@/pages/ReportsPage'), 'ReportsPage');
const FeedbackPage = lazyPage(() => import('@/pages/FeedbackPage'), 'FeedbackPage');
const ApprovalsPage = lazyPage(() => import('@/pages/ApprovalsPage'), 'ApprovalsPage');
const NotificationsPage = lazyPage(() => import('@/pages/NotificationsPage'), 'NotificationsPage');
const NotificationSettingsPage = lazyPage(
  () => import('@/pages/NotificationSettingsPage'),
  'NotificationSettingsPage',
);
const SettingsPage = lazyPage(() => import('@/pages/SettingsPage'), 'SettingsPage');
const OffersPage = lazyPage(() => import('@/pages/OffersPage'), 'OffersPage');
const NewOfferPage = lazyPage(() => import('@/pages/NewOfferPage'), 'NewOfferPage');
const OfferDetailPage = lazyPage(() => import('@/pages/OfferDetailPage'), 'OfferDetailPage');
const EditOfferPage = lazyPage(() => import('@/pages/EditOfferPage'), 'EditOfferPage');
const CatalogAdminPage = lazyPage(() => import('@/pages/CatalogAdminPage'), 'CatalogAdminPage');
const ContractsRegistryPage = lazyPage(
  () => import('@/pages/ContractsRegistryPage'),
  'ContractsRegistryPage',
);
const DocumentTemplatesPage = lazyPage(
  () => import('@/pages/DocumentTemplatesPage'),
  'DocumentTemplatesPage',
);
const ImportLeadsPage = lazyPage(() => import('@/pages/ImportLeadsPage'), 'ImportLeadsPage');
const UsagePage = lazyPage(() => import('@/pages/UsagePage'), 'UsagePage');
const RemindersAuditPage = lazyPage(
  () => import('@/pages/RemindersAuditPage'),
  'RemindersAuditPage',
);
const PmaxNoReactionReportPage = lazyPage(
  () => import('@/pages/PmaxNoReactionReportPage'),
  'PmaxNoReactionReportPage',
);
const ReportPage = lazyPage(() => import('@/pages/ReportPage'), 'ReportPage');
const NotFoundPage = lazyPage(() => import('@/pages/NotFoundPage'), 'NotFoundPage');

/** Każda strona dostaje własną stronę błędu — pasek i menu zostają, pada tylko środek. */
function withRouteErrors(routes: RouteObject[]): RouteObject[] {
  return routes.map((route) => ({ ...route, errorElement: <RouteError /> }));
}

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    errorElement: <RouteError />,
    children: withRouteErrors([
      {
        index: true,
        element: <Navigate to="/crm" replace />,
      },
      {
        path: 'crm',
        // Kanban potrzebuje pelnej wysokosci (kolumny przewijaja sie osobno) —
        // DashboardLayout daje lg:h-full tylko trasom z tym znacznikiem.
        handle: { fullHeight: true },
        element: (
          <Suspense fallback={<RouteLoader />}>
            <CrmPage />
          </Suspense>
        ),
      },
      {
        path: 'crm/nowy',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <LeadNewPage />
          </Suspense>
        ),
      },
      {
        path: 'crm/koszyk',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <BasketPage />
          </Suspense>
        ),
      },
      {
        // /:id po /koszyk i /nowy zeby Hono nie zlapal "koszyk"/"nowy" jako leadId
        path: 'crm/:id',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <LeadDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'sprzedazy',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <SalesDashboardPage />
          </Suspense>
        ),
      },
      {
        path: 'marketing',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <MarketingDashboardPage />
          </Suspense>
        ),
      },
      {
        path: 'projekty',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <ProjectsPage />
          </Suspense>
        ),
      },
      {
        path: 'projekty/:id',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <ProjectDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'realizacje',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <RealizationsPage />
          </Suspense>
        ),
      },
      {
        path: 'realizacje/:id',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <RealizationDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'raporty',
        handle: { tabs: 'reports' },
        element: (
          <Suspense fallback={<RouteLoader />}>
            <ReportsPage />
          </Suspense>
        ),
      },
      {
        path: 'uwagi',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <FeedbackPage />
          </Suspense>
        ),
      },
      {
        // Prośby o rabat i o usunięcie oferty — jedna lista (Dawid 2026-10-01).
        path: 'do-akceptacji',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <ApprovalsPage />
          </Suspense>
        ),
      },
      {
        // Stary adres (powiadomienia, zakładki w przeglądarce) → wspólna lista.
        path: 'rabaty',
        element: <Navigate to="/do-akceptacji?typ=rabaty" replace />,
      },
      {
        path: 'notyfikacje',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <NotificationsPage />
          </Suspense>
        ),
      },
      {
        path: 'notyfikacje/ustawienia',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <NotificationSettingsPage />
          </Suspense>
        ),
      },
      {
        path: 'oferty',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <OffersPage />
          </Suspense>
        ),
      },
      {
        path: 'oferty/nowa',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <NewOfferPage />
          </Suspense>
        ),
      },
      {
        // STATIC path PRZED /oferty/:id — inaczej Router zlapie 'prosby-o-usuniecie' jako offerId
        path: 'oferty/prosby-o-usuniecie',
        element: <Navigate to="/do-akceptacji?typ=usuniecia" replace />,
      },
      {
        path: 'oferty/:id',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <OfferDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'oferty/:id/edytuj',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <EditOfferPage />
          </Suspense>
        ),
      },
      {
        path: 'ustawienia',
        handle: { tabs: 'settings' },
        element: (
          <Suspense fallback={<RouteLoader />}>
            <SettingsPage />
          </Suspense>
        ),
      },
      {
        path: 'ustawienia/cennik',
        handle: { tabs: 'settings' },
        element: (
          <Suspense fallback={<RouteLoader />}>
            <CatalogAdminPage />
          </Suspense>
        ),
      },
      {
        path: 'ustawienia/szablony',
        handle: { tabs: 'settings' },
        element: (
          <Suspense fallback={<RouteLoader />}>
            <DocumentTemplatesPage />
          </Suspense>
        ),
      },
      {
        path: 'umowy',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <ContractsRegistryPage />
          </Suspense>
        ),
      },
      {
        path: 'ustawienia/import',
        handle: { tabs: 'tools' },
        element: (
          <Suspense fallback={<RouteLoader />}>
            <ImportLeadsPage />
          </Suspense>
        ),
      },
      {
        path: 'ustawienia/uzycie',
        handle: { tabs: 'tools' },
        element: (
          <Suspense fallback={<RouteLoader />}>
            <UsagePage />
          </Suspense>
        ),
      },
      {
        path: 'ustawienia/reminders-audit',
        handle: { tabs: 'tools' },
        element: (
          <Suspense fallback={<RouteLoader />}>
            <RemindersAuditPage />
          </Suspense>
        ),
      },
      {
        // Brak ekranu-galerii — landing ladujemy od razu w domyslnym raporcie
        // (Leady bez reakcji). Przelaczanie raportow przez taby u gory shella.
        path: 'analiza-leadow',
        element: <Navigate to="/analiza-leadow/no-reaction" replace />,
      },
      {
        path: 'analiza-leadow/pmax-bez-reakcji-2026-05-20',
        handle: { tabs: 'reports' },
        element: (
          <Suspense fallback={<RouteLoader />}>
            <PmaxNoReactionReportPage />
          </Suspense>
        ),
      },
      {
        // Generyczna strona raportu — :reportType (switch w ReportPage).
        // React Router: static routes (pmax-bez-reakcji) maja pierwszenstwo
        // nad :reportType (specyficznosc), wiec kolejnosc bezpieczna.
        path: 'analiza-leadow/:reportType',
        handle: { tabs: 'reports' },
        element: (
          <Suspense fallback={<RouteLoader />}>
            <ReportPage />
          </Suspense>
        ),
      },
      {
        path: '*',
        element: (
          <Suspense fallback={<RouteLoader />}>
            <NotFoundPage />
          </Suspense>
        ),
      },
    ]),
  },
]);

function RouteLoader() {
  // Ten sam wskaznik co w kazdej sekcji (components/ui/Loading) — jeden wyglad ladowania.
  return <Loading size="page" />;
}

export function AppRouter() {
  return <RouterProvider router={router} />;
}
